import { useCallback, useEffect, useState } from 'react'
import {
  approveApplication,
  getApplication,
  getApplicationCapStatus,
  getApplications,
  moveApplicationWaitlist,
  rejectApplication,
  type CapStatus,
  type CreatorShopApplicationDetail,
  type CreatorShopApplicationFilters,
  type CreatorShopApplicationStatus,
  type CreatorShopApplicationSummary,
} from '../../services/adminApi'
import { ApplicationCard } from '../../components/cards/ApplicationCard'
import { ApplicationDetailModal } from './ApplicationDetailModal'
import { CustomButton } from '../../components/common/CustomButton'

const statusOptions: { value: CreatorShopApplicationStatus | ''; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'canceled', label: 'Canceled' },
  { value: '', label: 'All statuses' },
]

export function Applications() {
  const [applications, setApplications] = useState<CreatorShopApplicationSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [capStatus, setCapStatus] = useState<CapStatus | null>(null)

  const [filters, setFilters] = useState<CreatorShopApplicationFilters>({
    status: 'pending',
    page: 1,
    page_size: 20,
  })

  const [selected, setSelected] = useState<CreatorShopApplicationDetail | null>(null)

  const loadCapStatus = useCallback(async () => {
    const res = await getApplicationCapStatus()
    if (res.ok && res.data) setCapStatus(res.data)
  }, [])

  const loadApplications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getApplications(filters)
      if (response.ok && response.data) {
        setApplications(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load applications')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadApplications()
  }, [loadApplications])

  useEffect(() => {
    loadCapStatus()
  }, [loadCapStatus])

  const handleStatusChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      status: value as CreatorShopApplicationStatus | '',
      page: 1,
    }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleOpen = async (summary: CreatorShopApplicationSummary) => {
    const response = await getApplication(summary.id)
    if (response.ok && response.data) {
      setSelected(response.data)
    } else {
      setError(response.error?.message || 'Failed to load application')
    }
  }

  const refreshAll = () => {
    loadApplications()
    loadCapStatus()
  }

  const handleApprove = async (decisionNotes?: string) => {
    if (!selected) return
    const res = await approveApplication(selected.id, decisionNotes)
    if (res.ok && res.data) {
      setSelected(res.data)
      refreshAll()
    } else {
      throw new Error(res.error?.message || 'Approval failed')
    }
  }

  const handleReject = async (category: string, decisionNotes?: string) => {
    if (!selected) return
    const res = await rejectApplication(selected.id, category, decisionNotes)
    if (res.ok && res.data) {
      setSelected(res.data)
      refreshAll()
    } else {
      throw new Error(res.error?.message || 'Rejection failed')
    }
  }

  const handleWaitlist = async (target: 'waitlisted' | 'pending') => {
    if (!selected) return
    const res = await moveApplicationWaitlist(selected.id, target)
    if (res.ok && res.data) {
      setSelected(res.data)
      refreshAll()
    } else {
      throw new Error(res.error?.message || 'Waitlist update failed')
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">
            Creator Shop Applications
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Early Shops Program review queue — {total} {filters.status || 'total'}
          </p>
        </div>
        {capStatus && (
          <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-5 py-3">
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Founding seats
            </p>
            <p className="text-2xl font-bold text-[#503864] dark:text-[#d0b8df]">
              {capStatus.used} <span className="text-base text-gray-400">/ {capStatus.cap}</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {capStatus.available} remaining
            </p>
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <select
          value={filters.status || ''}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No applications found</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {applications.map((app) => (
            <ApplicationCard key={app.id} application={app} onClick={handleOpen} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {selected && (
        <ApplicationDetailModal
          application={selected}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onWaitlist={handleWaitlist}
        />
      )}
    </div>
  )
}
